import { Grid, IconButton, Tooltip } from '@mui/material';
import { CustomCellRendererProps } from 'ag-grid-react';
import Link from 'next/link';
import { FC, ReactNode, useMemo } from 'react';

export interface TableAction {
  icon: ReactNode | ((data: unknown) => ReactNode);
  onClick?: (data: unknown) => void;
  href?: ((data: unknown) => string) | string;
  color?: 'primary' | 'secondary';
  target?: '_blank';
  disabled?: ((data: unknown) => boolean) | boolean;
  tooltip?: string;
}

interface ActionsRendererProps {
  actions: TableAction[];
}

const ActionsRenderer: FC<CustomCellRendererProps & ActionsRendererProps> = ({
  data,
  actions,
}) => {
  const actionViews = useMemo(() => {
    return actions.map(
      (
        {
          icon,
          onClick,
          target,
          href = '',
          color = 'primary',
          disabled = false,
          tooltip = '',
        },
        i
      ) => {
        const disabledValue =
          typeof disabled === 'boolean' ? disabled : disabled(data);

        const hrefValue = typeof href === 'string' ? href : href(data);

        if (href && hrefValue) {
          return (
            <Grid key={i}>
              <Link href={hrefValue} target={target}>
                <Tooltip title={tooltip} placement="top">
                  <IconButton
                    size="small"
                    color={color}
                    disabled={disabledValue}
                  >
                    {typeof icon === 'function' ? icon(data) : icon}
                  </IconButton>
                </Tooltip>
              </Link>
            </Grid>
          );
        }

        return (
          <Grid key={i}>
            <Tooltip title={tooltip} placement="top">
              <IconButton
                onClick={disabledValue ? undefined : () => onClick?.(data)}
                size="small"
                color={color}
                disabled={disabledValue}
              >
                {typeof icon === 'function' ? icon(data) : icon}
              </IconButton>
            </Tooltip>
          </Grid>
        );
      }
    );
  }, [actions, data]);

  return (
    <Grid container spacing={1} justifyContent="center">
      {actionViews}
    </Grid>
  );
};

export default ActionsRenderer;
